<template>
  <Base>
    <template v-slot:toolbar>
      <div class="toolbar" id="kt_toolbar">
        <div class="container-fluid d-flex flex-stack" id="kt_toolbar_container">
          <div class="d-flex align-items-center me-3">
            <span class="h-20px border-gray-200 border-start mx-4"></span>
            <ul class="breadcrumb breadcrumb-separatorless fw-bold fs-7 my-1">
              <li class="breadcrumb-item text-muted">
                <router-link :to="{ name: 'dashboard' }" class="text-muted text-hover-primary">
                  <i class="fad fa-home"></i>
                  {{ trans('Dashboard') }}
                </router-link>
              </li>
              <li class="breadcrumb-item">
                <span class="bullet bg-gray-300 w-5px h-2px"></span>
              </li>
              <li class="breadcrumb-item text-dark">
                Suivi des envois
              </li>
            </ul>
          </div>

          <div class="d-flex align-items-center py-1">
            <router-link :to="{ name: 'campaign-list' }" class="btn btn-sm btn-light-primary me-3">
              <i class="fad fa-bullhorn"></i>
              Campagnes
            </router-link>
            <button class="btn btn-sm btn-primary" @click="load">
              <i class="fad fa-sync"></i>
              Rafraichir
            </button>
          </div>
        </div>
      </div>
    </template>

    <template #content>
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-5">
        <div>
          <h1 class="fs-2 fw-bolder mb-1">
            <i class="fad fa-paper-plane text-primary me-2"></i>
            Suivi des envois
          </h1>
          <span class="text-muted">Emails, SMS et WhatsApp envoyés par le notifier (campagnes, relances, transactionnels)</span>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="row g-5 mb-5">
        <div class="col-md-3">
          <div class="card bg-light-primary">
            <div class="card-body py-4">
              <div class="d-flex align-items-center">
                <div class="symbol symbol-50px me-3">
                  <span class="symbol-label bg-primary">
                    <i class="fad fa-paper-plane text-white fs-2"></i>
                  </span>
                </div>
                <div>
                  <div class="fs-4 fw-bolder">{{ stats.total }}</div>
                  <div class="text-muted fs-7">Total</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card bg-light-success">
            <div class="card-body py-4">
              <div class="d-flex align-items-center">
                <div class="symbol symbol-50px me-3">
                  <span class="symbol-label bg-success">
                    <i class="fad fa-check text-white fs-2"></i>
                  </span>
                </div>
                <div>
                  <div class="fs-4 fw-bolder">{{ stats.sent }}</div>
                  <div class="text-muted fs-7">Envoyés</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card bg-light-danger">
            <div class="card-body py-4">
              <div class="d-flex align-items-center">
                <div class="symbol symbol-50px me-3">
                  <span class="symbol-label bg-danger">
                    <i class="fad fa-times text-white fs-2"></i>
                  </span>
                </div>
                <div>
                  <div class="fs-4 fw-bolder">{{ stats.failed }}</div>
                  <div class="text-muted fs-7">Échoués</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card bg-light-warning">
            <div class="card-body py-4">
              <div class="d-flex align-items-center">
                <div class="symbol symbol-50px me-3">
                  <span class="symbol-label bg-warning">
                    <i class="fad fa-clock text-white fs-2"></i>
                  </span>
                </div>
                <div>
                  <div class="fs-4 fw-bolder">{{ stats.pending }}</div>
                  <div class="text-muted fs-7">En attente</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats par type -->
      <div class="row g-5 mb-5">
        <div class="col-md-4">
          <div class="card">
            <div class="card-body py-3">
              <div class="d-flex align-items-center">
                <span class="badge badge-light-primary fs-5 me-3">
                  <i class="fad fa-envelope me-1"></i> Email
                </span>
                <div class="d-flex gap-3 ms-auto">
                  <span class="badge badge-light-success" title="Envoyés">{{ stats.email?.sent || 0 }}</span>
                  <span class="badge badge-light-danger" title="Échoués">{{ stats.email?.failed || 0 }}</span>
                  <span class="badge badge-light-warning" title="En attente">{{ stats.email?.pending || 0 }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card">
            <div class="card-body py-3">
              <div class="d-flex align-items-center">
                <span class="badge badge-light-warning fs-5 me-3">
                  <i class="fad fa-message-sms me-1"></i> SMS
                </span>
                <div class="d-flex gap-3 ms-auto">
                  <span class="badge badge-light-success" title="Envoyés">{{ stats.sms?.sent || 0 }}</span>
                  <span class="badge badge-light-danger" title="Échoués">{{ stats.sms?.failed || 0 }}</span>
                  <span class="badge badge-light-warning" title="En attente">{{ stats.sms?.pending || 0 }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card">
            <div class="card-body py-3">
              <div class="d-flex align-items-center">
                <span class="badge badge-light-success fs-5 me-3">
                  <i class="fab fa-whatsapp me-1"></i> WhatsApp
                </span>
                <div class="d-flex gap-3 ms-auto">
                  <span class="badge badge-light-success" title="Envoyés">{{ stats.whatsapp?.sent || 0 }}</span>
                  <span class="badge badge-light-danger" title="Échoués">{{ stats.whatsapp?.failed || 0 }}</span>
                  <span class="badge badge-light-warning" title="En attente">{{ stats.whatsapp?.pending || 0 }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Filtres -->
      <div class="card mb-5">
        <div class="card-body py-4">
          <div class="row g-3 align-items-center">
            <div class="col-md-3">
              <label class="form-label fw-bold">Canal</label>
              <select v-model="filters.type" class="form-select form-select-sm" @change="resetAndLoad">
                <option value="">Tous</option>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-bold">Statut</label>
              <select v-model="filters.status" class="form-select form-select-sm" @change="resetAndLoad">
                <option value="">Tous</option>
                <option value="CREATED">En attente</option>
                <option value="SENT">Envoyés</option>
                <option value="FAILED">Échoués</option>
                <option value="CANCELLED">Annulés</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label fw-bold">Campagne</label>
              <select v-model="filters.campaign" class="form-select form-select-sm" @change="resetAndLoad">
                <option value="">Toutes</option>
                <option value="none">Sans campagne</option>
                <option v-for="c in campaigns" :key="c.id" :value="String(c.id)">{{ c.name }}</option>
              </select>
            </div>
            <div class="col-md-2">
              <label class="form-label fw-bold">Par page</label>
              <select v-model="filters.limit" class="form-select form-select-sm" @change="resetAndLoad">
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
                <option :value="200">200</option>
              </select>
            </div>
            <div class="col-md-1 d-flex align-items-end">
              <button class="btn btn-sm btn-light-primary w-100" @click="resetFilters" title="Réinitialiser les filtres">
                <i class="fad fa-undo"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Liste des jobs -->
      <div class="card">
        <div class="card-header border-0 pt-5">
          <h3 class="card-title align-items-start flex-column">
            <span class="card-label fw-bolder fs-3 mb-1">Historique des envois</span>
            <span class="text-muted mt-1 fw-bold fs-7">{{ jobs.length }} envoi(s) affiché(s) sur {{ stats.total }}</span>
          </h3>
        </div>
        <div class="card-body py-3">
          <div class="table-responsive">
            <table class="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
              <thead>
                <tr class="fw-bolder text-muted">
                  <th class="min-w-50px">ID</th>
                  <th class="min-w-80px">Canal</th>
                  <th class="min-w-100px">Statut</th>
                  <th class="min-w-150px">Destinataires</th>
                  <th class="min-w-120px">Template</th>
                  <th class="min-w-150px">Campagne</th>
                  <th class="min-w-130px">Créé le</th>
                  <th class="min-w-130px">Traité le</th>
                  <th class="min-w-120px text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="jobs.length === 0">
                  <td colspan="9" class="text-center text-muted py-10">
                    <i class="fad fa-inbox fs-2x mb-3 d-block"></i>
                    Aucun envoi trouvé
                  </td>
                </tr>
                <tr v-for="job in jobs" :key="job.type + '_' + job.id">
                  <td>
                    <span class="text-dark fw-bolder">#{{ job.id }}</span>
                  </td>
                  <td>
                    <span :class="getTypeBadgeClass(job.type)" class="badge">
                      <i :class="getTypeIcon(job.type)" class="me-1"></i>
                      {{ job.type }}
                    </span>
                  </td>
                  <td>
                    <span :class="getStatusBadgeClass(job.status)" class="badge">
                      {{ getStatusLabel(job.status) }}
                    </span>
                  </td>
                  <td>
                    <div class="d-flex flex-column">
                      <template v-if="job.recipients && job.recipients.length > 0">
                        <span v-for="(recipient, idx) in job.recipients.slice(0, 2)" :key="idx" class="text-dark fs-7">
                          {{ recipient }}
                        </span>
                        <span v-if="job.recipients.length > 2" class="text-muted fs-8">
                          +{{ job.recipients.length - 2 }} autre(s)
                        </span>
                      </template>
                      <span v-else class="text-muted">-</span>
                    </div>
                  </td>
                  <td>
                    <span v-if="job.template" class="text-dark fs-7">{{ job.template.name }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <span v-if="job.campaign" class="badge badge-light-info">{{ job.campaign.name }}</span>
                    <span v-else class="text-muted fs-8">Sans campagne</span>
                  </td>
                  <td>
                    <span class="text-muted fs-7">{{ formatDate(job.createdAt) }}</span>
                  </td>
                  <td>
                    <span class="text-muted fs-7">{{ formatDate(job.processedAt) }}</span>
                  </td>
                  <td class="text-end">
                    <button class="btn btn-icon btn-light-primary btn-sm me-1" @click="showJobDetail(job)" title="Voir détails">
                      <i class="fad fa-eye"></i>
                    </button>
                    <button v-if="job.status === 'CREATED'" class="btn btn-icon btn-light-danger btn-sm me-1" @click="askCancel(job)" title="Annuler cet envoi">
                      <i class="fad fa-ban"></i>
                    </button>
                    <button v-if="job.status === 'FAILED' || job.status === 'CANCELLED'" class="btn btn-icon btn-light-warning btn-sm" @click="askRetry(job)" title="Relancer cet envoi">
                      <i class="fad fa-rotate-right"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="d-flex justify-content-between align-items-center mt-5" v-if="totalPages > 1">
            <div class="text-muted">
              Page {{ currentPage }} sur {{ totalPages }}
            </div>
            <div class="d-flex gap-2">
              <button class="btn btn-sm btn-light-primary" :disabled="!canGoPrev" @click="prevPage">
                <i class="fad fa-chevron-left"></i>
                Précédent
              </button>
              <button class="btn btn-sm btn-light-primary" :disabled="!canGoNext" @click="nextPage">
                Suivant
                <i class="fad fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal détails job -->
      <b-modal id="modal-job-detail" size="lg" centered title="Détails de l'envoi" hide-footer>
        <div v-if="selectedJob">
          <div class="row mb-4">
            <div class="col-4">
              <label class="form-label fw-bold">ID</label>
              <div>#{{ selectedJob.id }}</div>
            </div>
            <div class="col-4">
              <label class="form-label fw-bold">Canal</label>
              <div>
                <span :class="getTypeBadgeClass(selectedJob.type)" class="badge">{{ selectedJob.type }}</span>
              </div>
            </div>
            <div class="col-4">
              <label class="form-label fw-bold">Statut</label>
              <div>
                <span :class="getStatusBadgeClass(selectedJob.status)" class="badge">{{ getStatusLabel(selectedJob.status) }}</span>
              </div>
            </div>
          </div>
          <div class="row mb-4">
            <div class="col-4">
              <label class="form-label fw-bold">Créé le</label>
              <div>{{ formatDate(selectedJob.createdAt) }}</div>
            </div>
            <div class="col-4">
              <label class="form-label fw-bold">Traité le</label>
              <div>{{ formatDate(selectedJob.processedAt) }}</div>
            </div>
            <div class="col-4">
              <label class="form-label fw-bold">Campagne</label>
              <div>
                <span v-if="selectedJob.campaign">{{ selectedJob.campaign.name }} (#{{ selectedJob.campaign.id }})</span>
                <span v-else class="text-muted">Sans campagne</span>
              </div>
            </div>
          </div>
          <div class="mb-4">
            <label class="form-label fw-bold">Destinataires</label>
            <div class="bg-light rounded p-3">
              <div v-for="(recipient, idx) in selectedJob.recipients" :key="idx">{{ recipient }}</div>
              <span v-if="!selectedJob.recipients || selectedJob.recipients.length === 0" class="text-muted">Aucun</span>
            </div>
          </div>
          <div class="mb-4" v-if="selectedJob.template">
            <label class="form-label fw-bold">Template</label>
            <div>{{ selectedJob.template.name }} (ID: {{ selectedJob.template.id }})</div>
          </div>
          <!-- Aperçu du message réellement envoyé -->
          <div class="mb-4">
            <label class="form-label fw-bold">
              <i class="fad fa-message-lines me-1"></i>
              Message envoyé
            </label>
            <div v-if="previewLoading" class="text-muted">
              <i class="fad fa-spinner fa-spin me-1"></i> Chargement de l'aperçu…
            </div>
            <template v-else-if="preview">
              <div v-if="preview.kind === 'text'" class="bg-light rounded p-3" style="white-space: pre-wrap;">{{ preview.text }}</div>
              <template v-else-if="preview.kind === 'email'">
                <div class="fw-bold mb-2">Objet : {{ preview.subject || '-' }}</div>
                <iframe v-if="preview.html" :srcdoc="preview.html" class="w-100 border rounded" style="min-height: 350px; background: #fff;"></iframe>
                <div v-else class="text-muted">Template email introuvable sur le disque.</div>
              </template>
              <template v-else-if="preview.kind === 'whatsapp'">
                <div class="bg-light rounded p-3">
                  <div class="mb-2">
                    <span class="badge badge-light-success me-2"><i class="fab fa-whatsapp me-1"></i>Template Meta</span>
                    <strong>{{ preview.templateName }}</strong> ({{ preview.language }})
                  </div>
                  <div class="text-muted fs-8 mb-2">Le corps du message vit chez Meta — voici les paramètres injectés :</div>
                  <table class="table table-sm table-row-dashed mb-0" v-if="preview.parameters && preview.parameters.length">
                    <thead><tr class="fw-bold text-muted"><th>Bloc</th><th>Paramètre</th><th>Valeur envoyée</th></tr></thead>
                    <tbody>
                      <tr v-for="(p, i) in preview.parameters" :key="i">
                        <td>{{ p.type }}</td>
                        <td>{{ p.name }}</td>
                        <td class="fw-bold">{{ p.value }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-else class="text-muted">Aucun paramètre.</div>
                </div>
              </template>
              <div v-else-if="preview.kind === 'error'" class="bg-light-danger text-danger rounded p-3 fs-8">
                Aperçu indisponible : {{ preview.error }}
              </div>
            </template>
          </div>
          <div class="mb-4" v-if="selectedJob.data">
            <label class="form-label fw-bold">Données du job</label>
            <pre class="bg-light rounded p-3 fs-8" style="max-height: 250px; overflow: auto;">{{ JSON.stringify(selectedJob.data, null, 2) }}</pre>
          </div>
        </div>
      </b-modal>

      <!-- Modal confirmation annuler / relancer (jamais de confirm() natif) -->
      <b-modal id="modal-job-action" centered :title="pendingAction === 'cancel' ? 'Annuler cet envoi ?' : 'Relancer cet envoi ?'" hide-footer>
        <div v-if="actionJob">
          <p v-if="pendingAction === 'cancel'">
            L'envoi <strong>#{{ actionJob.id }}</strong> ({{ actionJob.type }}) ne sera <strong>jamais envoyé</strong>.
          </p>
          <p v-else>
            L'envoi <strong>#{{ actionJob.id }}</strong> ({{ actionJob.type }}) sera remis <strong>en attente</strong> et repris au prochain passage du traitement.
          </p>
          <div class="d-flex justify-content-end gap-2 mt-5">
            <button class="btn btn-sm btn-light" @click="$bvModal.hide('modal-job-action')">Retour</button>
            <button :class="['btn btn-sm', pendingAction === 'cancel' ? 'btn-danger' : 'btn-warning']" @click="confirmAction">
              <i :class="pendingAction === 'cancel' ? 'fad fa-ban' : 'fad fa-rotate-right'"></i>
              {{ pendingAction === 'cancel' ? 'Annuler l\'envoi' : 'Relancer' }}
            </button>
          </div>
        </div>
      </b-modal>
    </template>
  </Base>
</template>

<script lang="ts">
import Base from "@/layouts/Base.vue";
import { Vue, Component } from "vue-property-decorator";
import { api } from "@/services/Api";

@Component({
  components: { Base },
})
export default class JobMonitoring extends Vue {
  jobs: any[] = [];
  campaigns: any[] = [];
  stats: any = {
    total: 0,
    sent: 0,
    failed: 0,
    pending: 0,
    email: { total: 0, sent: 0, failed: 0, pending: 0 },
    sms: { total: 0, sent: 0, failed: 0, pending: 0 },
    whatsapp: { total: 0, sent: 0, failed: 0, pending: 0 },
  };
  filters = {
    type: "",
    status: "",
    campaign: "",
    limit: 50,
    offset: 0,
  };
  selectedJob: any = null;
  preview: any = null;
  previewLoading = false;
  actionJob: any = null;
  pendingAction: "cancel" | "retry" = "cancel";

  get currentPage(): number {
    return Math.floor(this.filters.offset / this.filters.limit) + 1;
  }

  get totalPages(): number {
    return Math.ceil(this.stats.total / this.filters.limit);
  }

  get canGoPrev(): boolean {
    return this.filters.offset > 0;
  }

  get canGoNext(): boolean {
    return this.filters.offset + this.filters.limit < this.stats.total;
  }

  mounted() {
    this.loadCampaigns();
    this.load();
  }

  async loadCampaigns() {
    const res = await api.get(api.com, "campaign/list");
    if (res && res.data && res.data.campaigns) {
      this.campaigns = res.data.campaigns;
    }
  }

  async load() {
    this.$store.commit("loading");

    let url = `job/list?limit=${this.filters.limit}&offset=${this.filters.offset}`;
    if (this.filters.type) {
      url += `&type=${this.filters.type}`;
    }
    if (this.filters.status) {
      url += `&status=${this.filters.status}`;
    }
    if (this.filters.campaign) {
      url += `&campaign=${this.filters.campaign}`;
    }

    const res = await api.get(api.com, url);

    if (res && res.data) {
      this.jobs = res.data.jobs || [];
      this.stats = res.data.stats || this.stats;
    }

    this.$store.commit("stopLoading");
  }

  resetAndLoad() {
    this.filters.offset = 0;
    this.load();
  }

  resetFilters() {
    this.filters = {
      type: "",
      status: "",
      campaign: "",
      limit: 50,
      offset: 0,
    };
    this.load();
  }

  prevPage() {
    if (this.canGoPrev) {
      this.filters.offset -= this.filters.limit;
      this.load();
    }
  }

  nextPage() {
    if (this.canGoNext) {
      this.filters.offset += this.filters.limit;
      this.load();
    }
  }

  showJobDetail(job: any) {
    this.selectedJob = job;
    this.preview = null;
    this.$bvModal.show("modal-job-detail");
    this.loadPreview(job);
  }

  // Aperçu du message RÉELLEMENT envoyé (template rendu avec les données du job).
  async loadPreview(job: any) {
    this.previewLoading = true;
    const res = await api.get(api.com, `job/show/${job.type}/${job.id}`);
    if (res && res.data && res.data.preview && this.selectedJob && this.selectedJob.id === job.id) {
      this.preview = res.data.preview;
    }
    this.previewLoading = false;
  }

  askCancel(job: any) {
    this.actionJob = job;
    this.pendingAction = "cancel";
    this.$bvModal.show("modal-job-action");
  }

  askRetry(job: any) {
    this.actionJob = job;
    this.pendingAction = "retry";
    this.$bvModal.show("modal-job-action");
  }

  async confirmAction() {
    if (!this.actionJob) {
      return;
    }
    const action = this.pendingAction === "cancel" ? "cancel" : "retry";
    const res = await api.get(api.com, `job/${action}/${this.actionJob.type}/${this.actionJob.id}`);
    this.$bvModal.hide("modal-job-action");
    this.actionJob = null;
    if (res && res.data && res.data.status === "success") {
      this.load();
    }
  }

  getTypeBadgeClass(type: string): string {
    switch (type?.toUpperCase()) {
      case "SMS":
        return "badge-light-warning";
      case "EMAIL":
        return "badge-light-primary";
      case "WHATSAPP":
        return "badge-light-success";
      default:
        return "badge-light-dark";
    }
  }

  getTypeIcon(type: string): string {
    switch (type?.toUpperCase()) {
      case "SMS":
        return "fad fa-message-sms";
      case "EMAIL":
        return "fad fa-envelope";
      case "WHATSAPP":
        return "fab fa-whatsapp";
      default:
        return "fad fa-paper-plane";
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status?.toUpperCase()) {
      case "SENT":
        return "badge-light-success";
      case "FAILED":
        return "badge-light-danger";
      case "CREATED":
        return "badge-light-warning";
      case "CANCELLED":
        return "badge-light-dark";
      default:
        return "badge-light-dark";
    }
  }

  getStatusLabel(status: string): string {
    switch (status?.toUpperCase()) {
      case "SENT":
        return "Envoyé";
      case "FAILED":
        return "Échoué";
      case "CREATED":
        return "En attente";
      case "CANCELLED":
        return "Annulé";
      default:
        return status;
    }
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}
</script>

<style scoped>
.symbol-label {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 0.475rem;
}
</style>
